/**
 * Focus Carousel — focus-carousel.js
 *
 * - scale() for center/side visual sizing (no layout shift)
 * - 2 clone sets each side so 10 slides never runs out of buffer
 * - Pure math offsets (all slides same width, step = slideW + gap)
 * - Reanchor fires only after transitionend, silently repositions
 * - Opacity tiers: center=100%, adjacent=80%, rest=50%
 */
(function () {
  'use strict';

  const EASE           = 'transform 0.42s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
  const DRAG_THRESHOLD = 6;
  const SWIPE_VELOCITY = 0.28;
  const CLONE_SETS     = 2; // clone sets on each side — 2×10=20 buffer slides each side

  const TIERS = [
    { scale: 1.05, opacity: 1.00, brightness: 1.00 }, // center — small scale keeps gaps even
    { scale: 0.95, opacity: 0.80, brightness: 0.93 }, // adjacent
    { scale: 0.88, opacity: 0.50, brightness: 0.82 }, // rest
  ];

  class FocusCarousel {
    constructor(el) {
      this.root       = el;
      this.track      = el.querySelector('[data-track]');
      this.origSlides = Array.from(el.querySelectorAll('[data-slide]'));
      this.dotsEl     = Array.from(el.querySelectorAll('[data-dot]'));
      this.prevBtn    = el.querySelector('[data-prev]');
      this.nextBtn    = el.querySelector('[data-next]');

      if (!this.track || this.origSlides.length === 0) return;

      this.count           = this.origSlides.length;
      this.currentTrackIdx = 0;
      this.offset          = 0;
      this.isTransitioning = false;

      this.dragging    = false;
      this.didDrag     = false;
      this.isHorizDrag = null;
      this.startX      = 0;
      this.startY      = 0;
      this.startOffset = 0;
      this.startTime   = 0;
      this.lastX       = 0;

      this._timer = null;
      this._delay = parseInt(el.dataset.autoplay, 10) || 0;

      this._buildClones();
      this._bindEvents();

      requestAnimationFrame(() => requestAnimationFrame(() => {
        this._cacheMetrics();
        this.currentTrackIdx = this.firstReal;
        this._applyTiers(this.currentTrackIdx);
        this._applyOffset(this.currentTrackIdx, false);
        this._updateDots();

        // Second recalculate after 300ms — catches mobile cases where
        // the viewport width isn't settled on first rAF (font loading,
        // scrollbar changes, mobile browser chrome shifting layout)
        setTimeout(() => {
          this._cacheMetrics();
          this._applyOffset(this.currentTrackIdx, false);
          this._startAutoplay();
        }, 300);
      }));
    }

    /* ─── Clone building ─────────────────────────────────────────
       Layout: [CLONE_SETS × N] [real 0..N-1] [CLONE_SETS × N]
       With CLONE_SETS=2, N=10: 20 buffer slides each side.
    ─────────────────────────────────────────────────────────────── */
    _buildClones() {
      for (let s = 0; s < CLONE_SETS; s++) {
        this.origSlides.forEach(slide => {
          const c = slide.cloneNode(true);
          c.classList.add('is-clone');
          c.removeAttribute('data-slide');
          this.track.appendChild(c);
        });
      }
      for (let s = 0; s < CLONE_SETS; s++) {
        [...this.origSlides].reverse().forEach(slide => {
          const c = slide.cloneNode(true);
          c.classList.add('is-clone');
          c.removeAttribute('data-slide');
          this.track.insertBefore(c, this.track.firstChild);
        });
      }

      this.allSlides    = Array.from(this.track.children);
      this.clonesBefore = CLONE_SETS * this.count;
      this.firstReal    = this.clonesBefore;
      this.lastReal     = this.clonesBefore + this.count - 1;
    }

    /* ─── Cache metrics — all slides same width (scale is visual only) ── */
    _cacheMetrics() {
      this.slideW = this.allSlides[0].offsetWidth;
      const raw   = getComputedStyle(this.root).getPropertyValue('--carousel-gap').trim();
      this.gap    = parseFloat(raw) || 16;
      this.step   = this.slideW + this.gap;
    }

    /* ─── Pure math offset ───────────────────────────────────── */
    _offsetForIdx(ti) {
      // Use track's parent width, not root, to get true visible container
      const containerW  = this.track.parentElement.offsetWidth;
      const slideCenter = ti * this.step + this.slideW / 2;
      return containerW / 2 - slideCenter;
    }

    _realIdx(ti) {
      return ((ti - this.clonesBefore) % this.count + this.count) % this.count;
    }

    /* ─── Apply scale/opacity tiers ─────────────────────────── */
    _applyTiers(ci) {
      this.allSlides.forEach((slide, i) => {
        const dist = Math.abs(i - ci);
        const tier = Math.min(dist, TIERS.length - 1);
        const t    = TIERS[tier];
        slide.style.transform = `scale(${t.scale})`;
        slide.style.opacity   = String(t.opacity);
        slide.style.filter    = `brightness(${t.brightness})`;
        slide.classList.toggle('is-center',   dist === 0);
        slide.classList.toggle('is-adjacent', dist === 1);
      });
    }

    /* ─── Continuous drag tiers ──────────────────────────────── */
    _applyDragTiers() {
      const cc = this.track.parentElement.offsetWidth / 2;
      this.allSlides.forEach((slide, i) => {
        const sc   = this.offset + i * this.step + this.slideW / 2;
        const dist = Math.abs(sc - cc);
        const t    = Math.min(dist / this.step, 2);

        let scale, opacity, brightness;
        if (t <= 1) {
          scale      = TIERS[0].scale      + t * (TIERS[1].scale      - TIERS[0].scale);
          opacity    = TIERS[0].opacity    + t * (TIERS[1].opacity    - TIERS[0].opacity);
          brightness = TIERS[0].brightness + t * (TIERS[1].brightness - TIERS[0].brightness);
        } else {
          const f    = t - 1;
          scale      = TIERS[1].scale      + f * (TIERS[2].scale      - TIERS[1].scale);
          opacity    = TIERS[1].opacity    + f * (TIERS[2].opacity    - TIERS[1].opacity);
          brightness = TIERS[1].brightness + f * (TIERS[2].brightness - TIERS[1].brightness);
        }
        slide.style.transform = `scale(${scale.toFixed(4)})`;
        slide.style.opacity   = opacity.toFixed(4);
        slide.style.filter    = `brightness(${brightness.toFixed(4)})`;
        slide.classList.remove('is-center', 'is-adjacent');
      });
    }

    _clearDragTiers() {
      this.allSlides.forEach(s => {
        s.style.transform = '';
        s.style.opacity   = '';
        s.style.filter    = '';
      });
    }

    /* ─── Apply offset ───────────────────────────────────────── */
    _applyOffset(ti, animate) {
      const target = this._offsetForIdx(ti);
      this.track.style.transition = animate ? EASE : 'none';
      if (animate) this.isTransitioning = true;
      this.offset = target;
      this.track.style.transform = `translateX(${target}px)`;
    }

    /* ─── Step ───────────────────────────────────────────────── */
    _step(delta, animate = true) {
      this.currentTrackIdx += delta;
      this._applyTiers(this.currentTrackIdx);
      this._applyOffset(this.currentTrackIdx, animate);
      this._updateDots();
    }

    /* ─── Go to real index ───────────────────────────────────── */
    _goToReal(realIdx, animate = true) {
      const base    = this.clonesBefore + realIdx;
      const options = [base - this.count, base, base + this.count];
      let best = base, bestDist = Infinity;
      options.forEach(ti => {
        const dist = Math.abs(ti - this.currentTrackIdx);
        if (dist < bestDist) { bestDist = dist; best = ti; }
      });
      this.currentTrackIdx = best;
      this._applyTiers(best);
      this._applyOffset(best, animate);
      this._updateDots();
    }

    /* ─── Reanchor ───────────────────────────────────────────── */
    _reanchor() {
      this.isTransitioning = false;
      if (this.currentTrackIdx < this.firstReal || this.currentTrackIdx > this.lastReal) {
        const realTi         = this.clonesBefore + this._realIdx(this.currentTrackIdx);
        this.currentTrackIdx = realTi;
        this._applyTiers(realTi);
        this.track.style.transition = 'none';
        this.offset                 = this._offsetForIdx(realTi);
        this.track.style.transform  = `translateX(${this.offset}px)`;
        void this.track.offsetWidth;
      }
    }

    /* ─── Dots ───────────────────────────────────────────────── */
    _updateDots() {
      const ri = this._realIdx(this.currentTrackIdx);
      this.dotsEl.forEach((d, i) => d.classList.toggle('is-active', i === ri));
    }

    /* ─── Closest idx ────────────────────────────────────────── */
    _closestTrackIdx() {
      const cc = this.track.parentElement.offsetWidth / 2;
      let best = this.currentTrackIdx, bestDist = Infinity;
      this.allSlides.forEach((_, i) => {
        const sc   = this.offset + i * this.step + this.slideW / 2;
        const dist = Math.abs(sc - cc);
        if (dist < bestDist) { bestDist = dist; best = i; }
      });
      return best;
    }

    /* ─── Autoplay ───────────────────────────────────────────── */
    _startAutoplay() {
      if (!this._delay || this.count < 2) return;
      this._clearAutoplay();
      this._timer = setInterval(() => this._step(1, true), this._delay);
    }
    _clearAutoplay() {
      if (this._timer) { clearInterval(this._timer); this._timer = null; }
    }
    _resetAutoplay() { this._clearAutoplay(); this._startAutoplay(); }

    /* ─── Drag ───────────────────────────────────────────────── */
    _clientX(e) { return e.touches ? e.touches[0].clientX : e.clientX; }
    _clientY(e) { return e.touches ? e.touches[0].clientY : e.clientY; }

    _onDragStart(e) {
      if (this.isTransitioning) {
        const matrix = new DOMMatrix(getComputedStyle(this.track).transform);
        this.track.style.transition = 'none';
        this.offset = matrix.m41;
        this.track.style.transform = `translateX(${this.offset}px)`;
        void this.track.offsetWidth;
        this.isTransitioning = false;
      }
      this.dragging    = true;
      this.didDrag     = false;
      this.isHorizDrag = null;
      this.startX      = this._clientX(e);
      this.startY      = this._clientY(e);
      this.lastX       = this.startX;
      this.startOffset = this.offset;
      this.startTime   = Date.now();
      this._clearAutoplay();
      this.track.classList.add('is-dragging');
    }

    _onDragMove(e) {
      if (!this.dragging) return;
      const x  = this._clientX(e);
      const y  = this._clientY(e);
      const dx = x - this.startX;
      const dy = y - this.startY;
      if (this.isHorizDrag === null && (Math.abs(dx) > 4 || Math.abs(dy) > 4)) {
        this.isHorizDrag = Math.abs(dx) > Math.abs(dy);
      }
      if (this.isHorizDrag === false) { this._cancelDrag(); return; }
      if (Math.abs(dx) > DRAG_THRESHOLD) this.didDrag = true;
      this.lastX  = x;
      this.offset = this.startOffset + dx;
      this.track.style.transition = 'none';
      this.track.style.transform  = `translateX(${this.offset}px)`;
      this._applyDragTiers();
    }

    _onDragEnd() {
      if (!this.dragging) return;
      this.dragging = false;
      this.track.classList.remove('is-dragging');
      if (this.isHorizDrag === false) return;
      const dx       = this.lastX - this.startX;
      const elapsed  = Date.now() - this.startTime;
      const velocity = Math.abs(dx) / elapsed;
      let targetTi   = this._closestTrackIdx();
      if (velocity > SWIPE_VELOCITY && Math.abs(dx) > DRAG_THRESHOLD) {
        targetTi = dx < 0 ? this.currentTrackIdx + 1 : this.currentTrackIdx - 1;
      }
      this._clearDragTiers();
      this.currentTrackIdx = targetTi;
      this._applyTiers(targetTi);
      this._applyOffset(targetTi, true);
      this._updateDots();
      this._resetAutoplay();
    }

    _cancelDrag() {
      this.dragging = false;
      this.track.classList.remove('is-dragging');
      this._clearDragTiers();
      this._applyTiers(this.currentTrackIdx);
      this._applyOffset(this.currentTrackIdx, true);
      this._resetAutoplay();
    }

    /* ─── Events ─────────────────────────────────────────────── */
    _bindEvents() {
      this.track.addEventListener('transitionend', e => {
        if (e.propertyName === 'transform') this._reanchor();
      });
      this.track.addEventListener('touchstart',  e => this._onDragStart(e), { passive: true });
      this.track.addEventListener('touchmove',   e => this._onDragMove(e),  { passive: true });
      this.track.addEventListener('touchend',    e => this._onDragEnd(e),   { passive: true });
      this.track.addEventListener('touchcancel', e => this._onDragEnd(e),   { passive: true });
      this.track.addEventListener('mousedown', e => this._onDragStart(e));
      window.addEventListener('mousemove',     e => this._onDragMove(e));
      window.addEventListener('mouseup',       e => this._onDragEnd(e));
      this.root.addEventListener('mouseenter', () => this._clearAutoplay());
      this.root.addEventListener('mouseleave', () => this._startAutoplay());
      if (this.prevBtn) this.prevBtn.addEventListener('click', () => {
        this._step(-1, true); this._resetAutoplay();
      });
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => {
        this._step(1, true); this._resetAutoplay();
      });
      this.dotsEl.forEach(dot => {
        dot.addEventListener('click', () => {
          this._goToReal(parseInt(dot.dataset.dot, 10), true);
          this._resetAutoplay();
        });
      });
      this.origSlides.forEach((slide, i) => {
        slide.addEventListener('click', () => {
          if (!this.didDrag && i !== this._realIdx(this.currentTrackIdx)) {
            this._goToReal(i, true); this._resetAutoplay();
          }
        });
      });
      this.root.setAttribute('tabindex', '0');
      this.root.addEventListener('keydown', e => {
        if (e.key === 'ArrowLeft')  { e.preventDefault(); this._step(-1, true); this._resetAutoplay(); }
        if (e.key === 'ArrowRight') { e.preventDefault(); this._step(1,  true); this._resetAutoplay(); }
      });
      let resizeTimer;
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
          this._cacheMetrics();
          this._applyOffset(this.currentTrackIdx, false);
        }, 150);
      });
      document.addEventListener('visibilitychange', () => {
        document.hidden ? this._clearAutoplay() : this._startAutoplay();
      });
    }
  }

  function initAll() {
    document.querySelectorAll('[data-carousel]').forEach(el => {
      if (!el._focusCarousel) el._focusCarousel = new FocusCarousel(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', () => {
    document.querySelectorAll('[data-carousel]').forEach(el => delete el._focusCarousel);
    initAll();
  });
  document.addEventListener('shopify:section:reorder', initAll);
  document.addEventListener('shopify:block:select', e => {
    const c = e.target.closest('[data-carousel]');
    if (c?._focusCarousel) {
      const idx = parseInt(e.target.dataset.slide, 10);
      if (!isNaN(idx)) c._focusCarousel._goToReal(idx, true);
    }
  });
})();
